import { SetGrid, type SetRow } from '../../sessions/SetGrid'
import { ArtifactCard } from './ArtifactCard'
import { formatArtifactDate } from './format'
import { ARTIFACT_CHIP_NEUTRAL, ARTIFACT_TITLE } from './styles'
import type { RecordArtifactData } from './types'

/**
 * The trend indicator: the verdict word from `classify_trend` ("LOAD UP",
 * "AT CEILING", "REGRESSION"…) as a chip, or nothing.
 *
 * **This card deliberately does not use the shared `TrendBadge`**, which is a
 * change from what Spec C originally planned. `TrendVerdict.pct` is required
 * and non-nullable — the badge renders `${pct}%` — and `trend_pct` was dropped
 * from the payload on 2026-08-07 (design §10) because the backend hardcoded it
 * to `None`, so the mockup's `+4% 4-WK` badge could never fire. With no source
 * for a percentage there is nothing to construct a verdict from.
 *
 * The accepted cost, stated when the ruling was taken: this card shows a word
 * where the Sessions view shows a percentage, so the two surfaces diverge.
 *
 * `trend_direction` is intentionally unconsumed here. It survives on the
 * payload for Spec E and for any later decision to tint this chip by
 * direction — worth knowing that a `REGRESSION` currently reads in the same
 * neutral grey as a `LOAD UP`.
 */
function RecordTrend({ data }: { data: RecordArtifactData }) {
  if (data.trend_label) {
    return <span style={ARTIFACT_CHIP_NEUTRAL}>{data.trend_label}</span>
  }
  return null
}

/**
 * `record` — the sets the user actually performed for one exercise on one day.
 *
 * The eyebrow says "Logged", not "Personal record": the payload carries no
 * personal-record flag, and decision D1 exists so a card can never assert
 * something the data does not support.
 */
export function RecordArtifactCard({ data }: { data: RecordArtifactData }) {
  // `performed_sets()` already drops BWx skipped sets server-side, so the
  // grid's `skipped` flag is never set from an artifact.
  // The duration fields are wired now, though this card only shows holds once
  // the artifact resolver sends them — that is Plan D's record.py change.
  // Wiring them here means the card needs no second visit.
  const rows: SetRow[] = data.sets.map((set) => ({
    label: String(set.index).padStart(2, '0'),
    reps: set.reps,
    weight: set.weight_kg,
    durationSec: set.duration_sec ?? null,
    metric: set.metric ?? 'reps',
    repsRight: null,
    durationSecRight: null,
  }))

  return (
    <ArtifactCard
      eyebrow={`Logged · ${formatArtifactDate(data.date)}`}
      action={data.href ? { label: 'Session →', href: data.href } : null}
      cardType="record"
    >
      <div className="flex items-center justify-between gap-3" style={{ marginBottom: 12 }}>
        <span style={ARTIFACT_TITLE}>{data.exercise}</span>
        <RecordTrend data={data} />
      </div>
      {rows.length > 0 ? <SetGrid rows={rows} idxLabel="Set" /> : null}
    </ArtifactCard>
  )
}
